import React, { useState } from 'react';
import Footer from './Footer';
import LandingHeader from './LandingHeader';

const SignInScreen = ({ onSignIn }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [selectedPersonality, setSelectedPersonality] = useState('professional');

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleTrialClick = (location) => {
    // Track event in Google Analytics
    if (window.gtag) {
      window.gtag('event', 'start_trial_click', {
        event_category: 'engagement',
        event_label: location,
        value: 1
      });
    }
    // Call the sign in function
    onSignIn();
  };

  const personalities = {
    professional: {
      label: 'Professional',
      icon: '🎯',
      activeClass: 'bg-blue-500 text-white shadow-md',
      inactiveClass: 'bg-blue-50 text-blue-700 hover:bg-blue-100',
      badgeClass: 'bg-blue-100 text-blue-700',
      response: "Hello John, I understand you're experiencing login issues. Let me help you resolve this. First, please try clearing your browser cache and cookies. If the issue persists, I can reset your password. Would you like me to send a reset link to your email?"
    },
    friendly: {
      label: 'Friendly',
      icon: '😊',
      activeClass: 'bg-green-500 text-white shadow-md',
      inactiveClass: 'bg-green-50 text-green-700 hover:bg-green-100',
      badgeClass: 'bg-green-100 text-green-700',
      response: "Hey John! 👋 Sorry to hear you're having trouble logging in. No worries though, we'll get you back in! Try clearing your browser cache first. If that doesn't work, I'll send you a password reset link right away. Sound good?"
    },
    empathetic: {
      label: 'Empathetic',
      icon: '💙',
      activeClass: 'bg-pink-500 text-white shadow-md',
      inactiveClass: 'bg-pink-50 text-pink-700 hover:bg-pink-100',
      badgeClass: 'bg-pink-100 text-pink-700',
      response: "Hi John, I'm really sorry you're having trouble accessing your account - I know how frustrating that can be! Let's get this sorted out for you right away. Please try clearing your browser cache, and if that doesn't help, I'll immediately send you a password reset link."
    },
    technical: {
      label: 'Technical',
      icon: '🔧',
      activeClass: 'bg-orange-500 text-white shadow-md',
      inactiveClass: 'bg-orange-50 text-orange-700 hover:bg-orange-100',
      badgeClass: 'bg-orange-100 text-orange-700',
      response: "Hello John, login errors are typically caused by cached authentication tokens. Please clear your browser cache (Ctrl+Shift+Del) and attempt to authenticate again. If the error persists, I can invalidate your current session and issue new credentials via email."
    },
    concise: {
      label: 'Concise',
      icon: '⚡',
      activeClass: 'bg-purple-500 text-white shadow-md',
      inactiveClass: 'bg-purple-50 text-purple-700 hover:bg-purple-100',
      badgeClass: 'bg-purple-100 text-purple-700',
      response: "Hi John, try clearing your browser cache. If that doesn't work, I'll send a password reset link. Let me know."
    }
  };

  const faqs = [
    {
      question: "How does the free trial work?",
      answer: "You get 3 days of full access to try out all features. No credit card required to start. After the trial, you can choose to subscribe or cancel anytime."
    },
    {
      question: "Is my Gmail data secure?",
      answer: "Absolutely. We use Google's official OAuth authentication and only request the minimum permissions needed. We never store your passwords and all data is encrypted."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Yes! You can cancel your subscription at any time from the settings. You'll retain access until the end of your billing period."
    },
    {
      question: "What's the difference between Basic and Pro?",
      answer: "Basic gives you the Kanban board and email management. Pro adds AI-powered email generation with custom knowledge base and multiple response personalities to save you hours of work."
    },
    {
      question: "How does AI email generation work?",
      answer: "Our AI reads the email thread context and generates professional responses based on your custom knowledge base. You can choose from 5 different personality styles and always edit before sending."
    },
    {
      question: "Do you offer refunds?",
      answer: "Yes, we offer a 30-day money-back guarantee. If you're not satisfied with Inbox Mate, contact us for a full refund within 30 days of your purchase."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <LandingHeader onSignIn={onSignIn} />

      <div className="mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20 sm:mb-24 md:mb-32">
          {/* Left Side - Content */}
          <div className="space-y-8 sm:space-y-10">
            <div className="space-y-4 sm:space-y-6">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight tracking-tight">
                Manage support emails like a pro
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed">
                Transform your Gmail inbox into a powerful Kanban board. Organize, respond, and track your support tickets effortlessly.
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4 sm:space-y-5">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 bg-blue-50 rounded-xl flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div className="flex-1 pt-0.5 sm:pt-1">
                  <h3 className="font-semibold text-gray-900 text-base sm:text-lg">Visual Kanban Board</h3>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 bg-blue-50 rounded-xl flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                </div>
                <div className="flex-1 pt-0.5 sm:pt-1">
                  <h3 className="font-semibold text-gray-900 text-base sm:text-lg">Rich Text Replies</h3>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 bg-blue-50 rounded-xl flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="flex-1 pt-0.5 sm:pt-1">
                  <h3 className="font-semibold text-gray-900 text-base sm:text-lg">Lightning Fast Workflow</h3>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="space-y-3 sm:space-y-4 pt-2 sm:pt-4">
              <button
                onClick={() => handleTrialClick('hero')}
                className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all flex items-center justify-center gap-2 sm:gap-3 text-base sm:text-lg shadow-lg hover:shadow-xl hover:scale-105 transform"
              >
                <span>Start 3 day free trial</span>
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
              <p className="text-xs sm:text-sm text-gray-500 flex items-center justify-center sm:justify-start gap-2">
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="text-center sm:text-left">Sign in with Google • Cancel anytime</span>
              </p>
            </div>
          </div>

          {/* Right Side - Product Preview */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Browser mockup */}
              <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
                {/* Browser chrome */}
                <div className="bg-gray-100 px-4 py-3 flex items-center gap-2 border-b border-gray-200">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <div className="flex-1 mx-4 bg-white rounded px-3 py-1 text-xs text-gray-500">
                    mail-board.app
                  </div>
                </div>

                {/* App preview */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 h-[400px]">
                  <div className="grid grid-cols-3 gap-3 h-full">
                    {/* Open Column */}
                    <div className="bg-gray-200 rounded-lg p-3">
                      <div className="text-xs font-semibold text-gray-600 mb-2">📥 Open</div>
                      <div className="space-y-2">
                        <div className="bg-white rounded p-2 shadow-sm">
                          <div className="h-2 bg-gray-300 rounded mb-1"></div>
                          <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                        </div>
                        <div className="bg-white rounded p-2 shadow-sm">
                          <div className="h-2 bg-gray-300 rounded mb-1"></div>
                          <div className="h-2 bg-gray-200 rounded w-2/3"></div>
                        </div>
                        <div className="bg-white rounded p-2 shadow-sm">
                          <div className="h-2 bg-gray-300 rounded mb-1"></div>
                          <div className="h-2 bg-gray-200 rounded w-2/3"></div>
                        </div>
                        <div className="bg-white rounded p-2 shadow-sm">
                          <div className="h-2 bg-gray-300 rounded mb-1"></div>
                          <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                        </div>
                      </div>
                    </div>

                    {/* In Progress Column */}
                    <div className="bg-yellow-100 rounded-lg p-3">
                      <div className="text-xs font-semibold text-yellow-800 mb-2">⏳ In Progress</div>
                      <div className="space-y-2">
                        <div className="bg-white rounded p-2 shadow-sm">
                          <div className="h-2 bg-gray-300 rounded mb-1"></div>
                          <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                        </div>
                        <div className="bg-white rounded p-2 shadow-sm">
                          <div className="h-2 bg-gray-300 rounded mb-1"></div>
                          <div className="h-2 bg-gray-200 rounded w-2/3"></div>
                        </div>
                      </div>
                    </div>

                    {/* Done Column */}
                    <div className="bg-green-100 rounded-lg p-3">
                      <div className="text-xs font-semibold text-green-800 mb-2">✅ Done</div>
                      <div className="space-y-2">
                        <div className="bg-white rounded p-2 shadow-sm">
                          <div className="h-2 bg-gray-300 rounded mb-1"></div>
                          <div className="h-2 bg-gray-200 rounded w-2/3"></div>
                        </div>
                        <div className="bg-white rounded p-2 shadow-sm">
                          <div className="h-2 bg-gray-300 rounded mb-1"></div>
                          <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                        </div>
                        <div className="bg-white rounded p-2 shadow-sm">
                          <div className="h-2 bg-gray-300 rounded mb-1"></div>
                          <div className="h-2 bg-gray-200 rounded w-2/3"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -z-10 top-10 -right-10 w-72 h-72 bg-blue-200 rounded-full opacity-20 blur-3xl"></div>
              <div className="absolute -z-10 -bottom-10 -left-10 w-72 h-72 bg-green-200 rounded-full opacity-20 blur-3xl"></div>
            </div>
          </div>
        </div>

      </div>

      {/* Transform Gmail Section */}
      <div id="features" className="py-16 sm:py-20 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-14 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 tracking-tight">Transform Your Gmail Inbox</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">Stop drowning in emails. Turn your chaotic inbox into an organized Kanban board in seconds.</p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-8 items-center">
              {/* Before - Gmail Inbox */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
                <div className="bg-red-50 px-4 py-3 border-b border-red-100">
                  <h3 className="text-sm font-semibold text-red-800 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                    </svg>
                    Gmail Inbox
                    <span className="ml-auto px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded">127 unread</span>
                  </h3>
                </div>
                <div className="p-4 space-y-2 bg-white">
                  {[
                    { name: 'Sarah Johnson', time: '2h ago', subject: 'Urgent: Need help with login', initials: 'SJ', bg: 'bg-pink-200', text: 'text-pink-800' },
                    { name: 'Mike Chen', time: '3h ago', subject: 'Question about pricing plans', initials: 'MC', bg: 'bg-blue-200', text: 'text-blue-800' },
                    { name: 'Emma Wilson', time: '4h ago', subject: 'Feature request for mobile app', initials: 'EW', bg: 'bg-purple-200', text: 'text-purple-800' },
                    { name: 'David Brown', time: '5h ago', subject: 'Payment issue - please help', initials: 'DB', bg: 'bg-green-200', text: 'text-green-800' }
                  ].map((email, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded border border-gray-200">
                      <div className={`w-8 h-8 ${email.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <span className={`text-xs font-semibold ${email.text}`}>{email.initials}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900 text-sm truncate">{email.name}</span>
                          <span className="text-xs text-gray-500">{email.time}</span>
                        </div>
                        <p className="text-xs text-gray-600 truncate">{email.subject}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Arrow */}
              <div className="flex justify-center lg:block">
                <div className="bg-blue-500 text-white rounded-full p-3 sm:p-4 shadow-lg">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 rotate-90 lg:rotate-0 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </div>

              {/* After - Inbox Mate */}
              <div className="bg-white rounded-xl shadow-xl overflow-hidden border-2 border-blue-400">
                <div className="bg-gradient-to-r from-blue-50 to-green-50 px-4 py-3 border-b border-blue-100">
                  <h3 className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                    Inbox Mate - Organized
                    <span className="ml-auto">✨</span>
                  </h3>
                </div>
                <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100">
                  <div className="grid grid-cols-3 gap-2">
                    {/* Open Column */}
                    <div className="bg-gray-200 rounded-lg p-2">
                      <div className="text-xs font-semibold text-gray-700 mb-2">📥 Open</div>
                      <div className="space-y-1.5">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="bg-white rounded p-2 shadow-sm">
                            <div className="h-1.5 bg-gray-300 rounded mb-1"></div>
                            <div className="h-1.5 bg-gray-200 rounded w-3/4"></div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* In Progress Column */}
                    <div className="bg-yellow-100 rounded-lg p-2">
                      <div className="text-xs font-semibold text-yellow-800 mb-2">⏳ Progress</div>
                      <div className="space-y-1.5">
                        {[1, 2].map((i) => (
                          <div key={i} className="bg-white rounded p-2 shadow-sm">
                            <div className="h-1.5 bg-gray-300 rounded mb-1"></div>
                            <div className="h-1.5 bg-gray-200 rounded w-2/3"></div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Done Column */}
                    <div className="bg-green-100 rounded-lg p-2">
                      <div className="text-xs font-semibold text-green-800 mb-2">✅ Done</div>
                      <div className="space-y-1.5">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div key={i} className="bg-white rounded p-2 shadow-sm">
                            <div className="h-1.5 bg-gray-300 rounded mb-1"></div>
                            <div className="h-1.5 bg-gray-200 rounded w-3/4"></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="mt-12 sm:mt-14 md:mt-16 grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-base sm:text-lg">Instant Clarity</h3>
                <p className="text-gray-600 text-sm">See the status of every support ticket at a glance</p>
              </div>

              <div className="text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-base sm:text-lg">Work Faster</h3>
                <p className="text-gray-600 text-sm">Drag, drop, and reply without switching contexts</p>
              </div>

              <div className="text-center sm:col-span-2 md:col-span-1">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-base sm:text-lg">Stay Organized</h3>
                <p className="text-gray-600 text-sm">Never lose track of customer conversations again</p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 sm:mt-14 md:mt-16 flex flex-col items-center">
              <button
                onClick={() => handleTrialClick('features')}
                className="w-full sm:w-auto bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-lg transition-all flex items-center justify-center gap-2 sm:gap-3 text-base sm:text-lg shadow-lg hover:shadow-xl hover:scale-105 transform"
              >
                <span>Start 3 day free trial</span>
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
              <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-2 mt-3 sm:mt-4">
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span>Sign in with Google • Cancel anytime</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* AI Replies Section */}
      <div className="py-16 sm:py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-14 md:mb-16">
            <div className="flex items-center justify-center mb-2 sm:mb-3">
              <span className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-xs sm:text-sm font-bold px-3 py-1 rounded-full uppercase tracking-wide">PRO</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 tracking-tight px-4">AI-Powered Email Responses</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">Choose from 5 different personalities to match your brand voice. Our AI uses your custom knowledge base to generate contextual, accurate responses.</p>
          </div>

          <div className="max-w-5xl mx-auto px-4">
            {/* Sample Customer Email */}
            <div className="bg-gray-50 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-gray-200">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-semibold text-xs sm:text-sm">JD</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
                    <span className="font-semibold text-gray-900 text-sm sm:text-base">John Doe</span>
                    <span className="text-gray-500 text-xs sm:text-sm">john@example.com</span>
                  </div>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base">Hi, I'm having trouble accessing my account. I keep getting an error message when I try to log in. Can you help?</p>
                </div>
              </div>
            </div>

            {/* Personality Selector */}
            <div className="mb-6 sm:mb-8">
              <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-3 sm:mb-4">Select Response Personality:</label>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {Object.entries(personalities).map(([key, personality]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedPersonality(key)}
                    className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg font-medium text-xs sm:text-sm transition-all flex items-center gap-1.5 sm:gap-2 ${
                      selectedPersonality === key ? personality.activeClass : personality.inactiveClass
                    }`}
                  >
                    <span className="text-base sm:text-lg">{personality.icon}</span>
                    <span>{personality.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* AI Generated Response */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-500 rounded-xl p-4 sm:p-6 shadow-xl relative overflow-hidden">
              {/* Decorative gradient orb */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full opacity-10 blur-2xl"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-full opacity-10 blur-2xl"></div>

              <div className="relative flex items-start gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="font-semibold text-gray-900 text-sm sm:text-base">Your AI Mate</span>
                    <span className={`px-2 sm:px-3 py-0.5 sm:py-1 text-xs font-semibold rounded-full ${personalities[selectedPersonality].badgeClass}`}>
                      {personalities[selectedPersonality].icon} {personalities[selectedPersonality].label}
                    </span>
                  </div>
                  <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{personalities[selectedPersonality].response}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Knowledge Base Note */}
            <div className="mt-6 sm:mt-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 border border-blue-200 shadow-sm">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 mb-1.5 sm:mb-2 text-sm sm:text-base">Powered by Your Knowledge Base</h3>
                  <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">All responses are generated using your custom knowledge base, ensuring accurate information about your products, policies, and procedures. The AI adapts each personality while maintaining factual consistency.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing" className="py-16 sm:py-20 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12 sm:mb-14 md:mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 tracking-tight px-4">Simple, Transparent Pricing</h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 px-4">Start with a 3-day free trial.</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto px-4">
              {/* Basic Plan */}
              <div className="bg-white rounded-2xl border border-gray-200 p-10 hover:shadow-lg transition-shadow flex flex-col">
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">Basic Plan</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-gray-900">$2.99</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  <p className="text-sm text-blue-500 font-medium mt-3">Includes 3-day free trial</p>
                </div>

                <ul className="space-y-4 mb-10 flex-grow">
                  <li className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Full Kanban board access</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Gmail integration</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Email management</span>
                  </li>
                </ul>

                <div className="mt-auto">
                  <button
                    onClick={() => handleTrialClick('pricing_basic')}
                    className="w-full py-3.5 px-6 border-2 border-gray-200 text-gray-900 font-semibold rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all hover:scale-105 transform flex items-center justify-center gap-2"
                  >
                    <span>Start 3 day free trial</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                  <p className="text-center text-sm text-gray-500 mt-3">$0.00 due today, cancel anytime</p>
                </div>
              </div>

              {/* Pro Plan */}
              <div className="bg-white rounded-2xl border-2 border-blue-500 p-10 relative hover:shadow-xl transition-shadow flex flex-col">
                <div className="absolute -top-3 left-8">
                  <span className="bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide">Popular</span>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">Pro Plan</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-gray-900">$9.99</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  <p className="text-sm text-blue-500 font-medium mt-3">Includes 3-day free trial</p>
                </div>

                <ul className="space-y-4 mb-10 flex-grow">
                  <li className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Everything in Basic</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-900 font-semibold">AI email generation</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Custom knowledge base</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">5 response personalities</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">Priority support</span>
                  </li>
                </ul>

                <div className="mt-auto">
                  <button
                    onClick={() => handleTrialClick('pricing_pro')}
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl hover:scale-105 transform flex items-center justify-center gap-2"
                  >
                    <span>Start 3 day free trial</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                  <p className="text-center text-sm text-gray-500 mt-3">$0.00 due today, cancel anytime</p>
                </div>
              </div>
            </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="py-16 sm:py-20 md:py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12 sm:mb-14 md:mb-16">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 tracking-tight">Frequently Asked Questions</h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-600">Everything you need to know about Inbox Mate</p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg border border-gray-200 overflow-hidden transition-all"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full text-left px-6 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 pr-8">{faq.question}</h3>
                    <svg
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-6 pb-5 pt-0">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SignInScreen;